import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Icon } from '../lib/icons.js';

export function Ambient(): ReactNode {
  return (
    <div className="ambient" aria-hidden="true">
      <div className="grain" />
    </div>
  );
}

export function Panel({
  children,
  title,
  icon,
  className = '',
  action,
  note,
}: {
  children: ReactNode;
  title?: string;
  icon?: string;
  className?: string;
  action?: ReactNode;
  note?: string | undefined;
}): ReactNode {
  return (
    <section className={`glass panel ${className}`}>
      {title && (
        <header className="panel-title">
          {icon && <Icon name={icon} size={14} strokeWidth={1.9} />}
          <span>{title}</span>
          {action && (
            <>
              <span className="spacer" />
              {action}
            </>
          )}
        </header>
      )}
      {note && (
        <p className="panel-note">
          <Icon name="info" size={13} />
          <span>{note}</span>
        </p>
      )}
      {children}
    </section>
  );
}

export function Toggle({
  on,
  busy = false,
  disabled = false,
  onChange,
  label,
}: {
  on: boolean;
  busy?: boolean;
  disabled?: boolean;
  onChange: () => void;
  label: string;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className="toggle"
      data-on={on}
      data-busy={busy}
      disabled={disabled}
      onClick={onChange}
    />
  );
}

/**
 * A drag-anywhere slider. The whole track is the control (no separate thumb),
 * which is far easier to hit on a phone than a 20px knob and reads better as a
 * single pane of glass filling up.
 */
export function Slider({
  value,
  min = 0,
  max = 100,
  step = 1,
  label,
  format,
  onCommit,
  disabled = false,
}: {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  label: string;
  format?: (v: number) => string;
  onCommit: (value: number) => void;
  disabled?: boolean;
}): ReactNode {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [draft, setDraft] = useState(value);

  // While dragging, the local draft wins; otherwise follow the house.
  useEffect(() => {
    if (!dragging) setDraft(value);
  }, [value, dragging]);

  const valueAt = useCallback(
    (clientX: number): number => {
      const el = trackRef.current;
      if (!el) return draft;
      const rect = el.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const raw = min + ratio * (max - min);
      return Math.round(raw / step) * step;
    },
    [draft, min, max, step],
  );

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    setDraft(valueAt(e.clientX));
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (!dragging) return;
    setDraft(valueAt(e.clientX));
  };

  const finish = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (!dragging) return;
    setDragging(false);
    const next = valueAt(e.clientX);
    setDraft(next);
    if (next !== value) onCommit(next);
  };

  const pct = ((draft - min) / (max - min)) * 100;

  return (
    <div
      ref={trackRef}
      className="slider"
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-valuenow={Math.round(draft)}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-disabled={disabled}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onKeyDown={(e) => {
        if (disabled) return;
        const delta = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
        if (delta === 0) return;
        e.preventDefault();
        const next = Math.min(max, Math.max(min, draft + delta));
        setDraft(next);
        onCommit(next);
      }}
      style={disabled ? { opacity: 0.45 } : undefined}
    >
      <div className="slider-fill" style={{ width: `${pct}%` }} />
      <div className="slider-label">
        <span>{label}</span>
        <span className="val">{format ? format(draft) : Math.round(draft)}</span>
      </div>
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T | null;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
}): ReactNode {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Badge({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: 'positive' | 'caution' | 'alert';
}): ReactNode {
  return (
    <span className="badge" {...(tone ? { 'data-tone': tone } : {})}>
      {children}
    </span>
  );
}

export function Toast({ message, onDismiss }: { message: string; onDismiss: () => void }): ReactNode {
  useEffect(() => {
    const t = window.setTimeout(onDismiss, 4200);
    return () => window.clearTimeout(t);
  }, [message, onDismiss]);

  return (
    <div className="glass toast" role="status">
      <Icon name="info" size={17} />
      <span>{message}</span>
      <button type="button" className="icon-btn" style={{ width: 26, height: 26 }} onClick={onDismiss}>
        <Icon name="close" size={13} />
      </button>
    </div>
  );
}
